<template>
  <PageLayout>
    <template #headerTitle>Gestion des frais</template>

    <VBusHelper :emitOnMounted="$constants.EVENTS.FEE.LIST_ALL.REFRESH" />

    <!-- COUNTRY FILTER -->
    <div class="mb-4">
      <label class="block mb-2 text-sm font-medium text-gray-700">
        Pays
      </label>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="(country, code) in $constants.FEE.COUNTRIES"
          :key="code"
          @click="selectedCountry = code"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition-all',
            selectedCountry === code
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          ]"
        >
          {{ country.name }}
        </button>
      </div>
    </div>

    <!-- CATEGORY FILTER -->
    <div class="mb-6">
      <label class="block mb-2 text-sm font-medium text-gray-700">
        Groupe d'opérations
      </label>
      <div class="flex flex-wrap gap-2">
        <button
          @click="selectedCategory = ''"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2',
            selectedCategory === ''
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          ]"
        >
          📋 Tous les groupes
        </button>
        <button
          v-for="(catInfo, catKey) in $constants.FEE.CATEGORIES"
          :key="catKey"
          @click="selectedCategory = catKey"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2',
            selectedCategory === catKey
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          ]"
        >
          {{ catInfo.icon }} {{ catInfo.label }}
        </button>
      </div>
    </div>

    <!-- ERROR STATE -->
    <FeeLoadingError
      v-if="$stores.fee?.state?.list_all?.error"
      @refresh="() => $bus.emit($constants.EVENTS.FEE.LIST_ALL.REFRESH)"
    >
      <template #errorDescription>
        {{ $stores.fee?.state?.list_all?.error }}
      </template>
    </FeeLoadingError>

    <!-- LOADING STATE (only if no data yet) -->
    <div
      v-else-if="$stores.fee?.state?.list_all?.loading && !hasAnyFees"
      class="space-y-3"
    >
      <div
        v-for="i in 5"
        :key="i"
        class="bg-white rounded-xl p-4 shimmerContainer"
      >
        <div class="h-4 bg-gray-200 rounded shimmerTarget w-3/4"></div>
      </div>
    </div>

    <!-- FEES BY CATEGORY -->
    <div v-else-if="!$stores.fee?.state?.list_all?.loading || hasAnyFees" class="space-y-4">
      <div
        v-for="(catInfo, catKey) in $constants.FEE.CATEGORIES"
        :key="catKey"
        v-show="getCategoryFees(catKey).length > 0"
        class="bg-white rounded-2xl shadow-sm overflow-hidden"
      >
        <!-- CATEGORY HEADER -->
        <div
          @click="toggleCategory(catKey)"
          class="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
          :class="{ 'border-b-0': !expandedCategories[catKey] }"
        >
          <div class="flex items-center gap-3">
            <span class="text-2xl">{{ catInfo.icon }}</span>
            <div>
              <h4 class="font-semibold text-gray-800">{{ catInfo.label }}</h4>
              <p class="text-sm text-gray-500">
                {{ getCategoryFees(catKey).length }} opération(s)
              </p>
            </div>
          </div>
          <svg
            class="w-5 h-5 text-gray-400 transition-transform"
            :class="{ 'rotate-180': expandedCategories[catKey] }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <!-- CATEGORY CONTENT -->
        <div v-show="expandedCategories[catKey]" class="p-4">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase">
                    Opération
                  </th>
                  <th class="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase">
                    Marge
                  </th>
                  <th class="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase">
                    Processeur
                  </th>
                  <th class="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase">
                    Total
                  </th>
                  <th class="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr
                  v-for="fee in getCategoryFees(catKey)"
                  :key="fee.country + '-' + fee.operation"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <!-- OPERATION -->
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-2">
                      <span class="text-lg">{{ $constants.FEE.COUNTRIES[fee.country]?.flag }}</span>
                      <div>
                        <div class="text-base font-medium text-gray-900">
                          {{ $constants.FEE.OPERATIONS[fee.operation]?.label }}
                        </div>
                        <div class="text-sm text-gray-500">
                          {{ $constants.FEE.COUNTRIES[fee.country]?.name }}
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- PROFIT FEE -->
                  <td class="px-4 py-3 text-center">
                    <span
                      class="inline-block px-2 py-1 text-sm font-medium rounded-lg"
                      :class="feeBgClass('profitFee', fee.profitFee)"
                    >
                      {{ fee.profitFee }}%
                    </span>
                  </td>

                  <!-- PROCESSOR FEE -->
                  <td class="px-4 py-3 text-center">
                    <span
                      class="inline-block px-2 py-1 text-sm font-medium rounded-lg"
                      :class="feeBgClass('processorFee', fee.processorFee)"
                    >
                      {{ fee.processorFee }}%
                    </span>
                  </td>

                  <!-- TOTAL -->
                  <td class="px-4 py-3 text-center">
                    <span class="inline-block px-2 py-1 text-sm font-bold rounded-lg bg-primary-100 text-primary-700">
                      {{ feeTotal(fee) }}%
                    </span>
                  </td>

                  <!-- ACTIONS -->
                  <td class="px-4 py-3 text-center">
                    <button
                      class="px-3 py-1.5 text-sm font-medium rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all"
                      @click="editFee(fee)"
                    >
                      Modifier
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- EMPTY STATE -->
    <EmptyFees
      v-if="!$stores.fee?.state?.list_all?.loading && !hasAnyFees"
      @refresh="() => $bus.emit($constants.EVENTS.FEE.LIST_ALL.REFRESH)"
    />
  </PageLayout>
</template>

<script>
export default {
  name: 'FeesPage',

  data() {
    return {
      selectedCountry: '',
      selectedCategory: '',
      expandedCategories: {}
    };
  },

  mounted() {
    // Initialize with first country
    const countries = Object.keys(this.$constants.FEE.COUNTRIES);
    if (countries.length > 0) {
      this.selectedCountry = countries[0];
    }

    // Initialize all categories as collapsed
    Object.keys(this.$constants.FEE.CATEGORIES).forEach(catKey => {
      this.expandedCategories[catKey] = false;
    });
  },

  computed: {
    filteredFees() {
      let fees = this.$stores.fee?.state?.list_all?.list || [];

      if (this.selectedCountry) {
        fees = fees.filter(f => f.country === this.selectedCountry);
      }

      if (this.selectedCategory) {
        fees = fees.filter(f => {
          const opInfo = this.$constants.FEE.OPERATIONS[f.operation];
          return opInfo && opInfo.category === this.selectedCategory;
        });
      }

      return fees;
    },

    hasAnyFees() {
      return this.filteredFees.length > 0;
    }
  },

  methods: {
    getCategoryFees(categoryKey) {
      return this.filteredFees.filter(fee => {
        const opInfo = this.$constants.FEE.OPERATIONS[fee.operation];
        return opInfo && opInfo.category === categoryKey;
      });
    },

    getCategoryFeesCount(categoryKey) {
      return (this.$stores.fee?.state?.list_all?.list || []).filter(fee => {
        const opInfo = this.$constants.FEE.OPERATIONS[fee.operation];
        return opInfo && opInfo.category === categoryKey;
      }).length;
    },

    getCountryFeesCount(countryCode) {
      return (this.$stores.fee?.state?.list_all?.list || [])
        .filter(f => f.country === countryCode).length;
    },

    toggleCategory(catKey) {
      this.expandedCategories[catKey] = !this.expandedCategories[catKey];
    },

    expandAll() {
      Object.keys(this.expandedCategories).forEach(key => {
        this.expandedCategories[key] = true;
      });
    },

    collapseAll() {
      Object.keys(this.expandedCategories).forEach(key => {
        this.expandedCategories[key] = false;
      });
    },

    editFee(fee) {
      this.$bus.emit(this.$constants.EVENTS.FEE.CREATE_OR_UPDATE.SHOW, fee);
    },

    feeTotal(fee) {
      return (fee.serviceFee || 0).toFixed(1);
    },

    feeBgClass(type, value) {
      if (value === 0) return 'bg-gray-100 text-gray-600';
      if (value < 3) return 'bg-green-100 text-green-700';
      if (value < 6) return 'bg-yellow-100 text-yellow-700';
      if (value < 10) return 'bg-orange-100 text-orange-700';
      return 'bg-red-100 text-red-700';
    }
  }
};
</script>
